import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Input,
  Button,
  Grid,
  Snackbar,
  CssBaseline,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  Stack,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Textarea,
} from '@mui/joy';
import {
  Search as SearchIcon,
  KeyboardArrowRight,
  Edit as EditIcon,
  Delete as DeleteIcon,
  WarningRounded,
  Person as PersonIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { Select, Option } from '@mui/joy';
import Link from '@mui/joy/Link';
import { DataGrid } from '@mui/x-data-grid';
import { dataGridNativeCheckboxSlots } from '../../main/components/dataGridNativeCheckboxSlots';
import { getReceiverDetails, updateReceiverDetail, deleteReceiverDetail, createReceiverDetail, exportReceiverDetailsToExcel } from '../../main/API/receiverDetails';
import { fetchTagsQueryParamFromNames } from '../../main/API/shipmentTags';
import PermissionGate from '../../main/components/PermissionGate';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { usePermissions } from '../../main/hooks/usePermissions';
import { DirectionalMaterialThemeProvider } from '../../main/theme/DirectionalMaterialThemeProvider';
import { translateApiErrorData, translateAxiosErrorMessage, translateAggregateBackendMessage } from '../../main/utils/masterApiError';
import CustomerOptionList from '../../main/widgets/CustomersOptionList';
import PaymentStatusOptionList, { formatPaymentStatus, getPaymentStatusIconConfig } from '../../main/widgets/PaymentStatusOptionList';
import ShipmentTagsOptionList from '../../main/widgets/ShipmentTagsOptionList';
import { getPaginatedMeta } from '../../main/utils/pagination';

const DESTINATION_LOCATION_TYPE_VALUES = ['home', 'store', 'branch'];

export default function ReceiverDetailsScreen() {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const canListCustomers = hasPermission(PERMISSIONS.CUSTOMERS_LIST);

  const destinationLocationTypeOptions = useMemo(
    () =>
      DESTINATION_LOCATION_TYPE_VALUES.map((value) => ({
        value,
        label:
          value === 'home'
            ? t('customer.destinationHome')
            : value === 'store'
              ? t('customer.destinationStore')
              : t('customer.destinationBranch'),
      })),
    [t]
  );

  const formatDestinationLocationTypeCell = useCallback(
    (value) => {
      if (!value || typeof value !== 'string') return '-';
      const key = value.toLowerCase().trim();
      const found = destinationLocationTypeOptions.find((o) => o.value === key);
      return found ? found.label : value;
    },
    [destinationLocationTypeOptions]
  );
  const [loading, setLoading] = useState(false);
  const [receiverDetails, setReceiverDetails] = useState([]);
  const [data, setData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });
  const [filters, setFilters] = useState({
    search: '',
    id: '',
    shipment: '',
    shipper_reference: '',
    tracking_number: '',
    receiver_name: '',
    receiver_phone_number: '',
    receiver_civil_id: '',
    destination_location_type: '',
    destination_location: '',
    payment_status: '',
    owner_name: '',
    owner_phone_number: '',
    owner_civil_id: '',
    deleted: false,
    modified_at_start: '',
    modified_at_end: '',
    ordering: '-id',
  });
  const [filterOwner, setFilterOwner] = useState(null);
  const [tagFilterNames, setTagFilterNames] = useState([]);

  const orderingOptions = useMemo(
    () => [
      { value: '-id', label: t('receiverDetailsPage.orderIdDesc') },
      { value: 'id', label: t('receiverDetailsPage.orderIdAsc') },
      { value: '-created_at', label: t('receiverDetailsPage.orderCreatedDesc') },
      { value: 'created_at', label: t('receiverDetailsPage.orderCreatedAsc') },
      { value: '-modified_at', label: t('receiverDetailsPage.orderModifiedDesc') },
      { value: 'modified_at', label: t('receiverDetailsPage.orderModifiedAsc') },
      { value: 'receiver_name', label: t('receiverDetailsPage.orderReceiverNameAsc') },
      { value: '-receiver_name', label: t('receiverDetailsPage.orderReceiverNameDesc') },
      { value: 'shipment__tracking_number', label: t('receiverDetailsPage.orderTrackingAsc') },
      { value: '-shipment__tracking_number', label: t('receiverDetailsPage.orderTrackingDesc') },
      { value: 'shipment__shipper_reference', label: t('receiverDetailsPage.orderShipperRefAsc') },
      { value: '-shipment__shipper_reference', label: t('receiverDetailsPage.orderShipperRefDesc') },
      { value: '-shipment__price', label: t('receiverDetailsPage.orderPriceDesc') },
      { value: 'shipment__price', label: t('receiverDetailsPage.orderPriceAsc') },
    ],
    [t]
  );
  
  // Notification states
  const [failedNotification, setFailedNotification] = useState(false);
  const [successNotification, setSuccessNotification] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');

  // Edit modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingReceiverDetail, setEditingReceiverDetail] = useState(null);
  const [editForm, setEditForm] = useState({
    shipment: '',
    receiver_name: '',
    receiver_phone_number: '',
    receiver_civil_id: '',
    destination_location_type: '',
    destination_location: '',
  });
  const [editFormErrors, setEditFormErrors] = useState({});

  // Create modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    shipment: '',
    receiver_name: '',
    receiver_phone_number: '',
    receiver_civil_id: '',
    destination_location_type: '',
    destination_location: '',
  });
  const [createFormErrors, setCreateFormErrors] = useState({});

  // Delete modal states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingReceiverDetail, setDeletingReceiverDetail] = useState(null);

  // Export states
  const [selectedRows, setSelectedRows] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);

  const buildListParams = useCallback(async ({ includePagination = true } = {}) => {
    const params = {};

    if (includePagination) {
      params.page = paginationModel.page + 1;
      params.page_size = paginationModel.pageSize;
      params.pagination = true;
    }

    if (filters.search) params.search = filters.search;
    if (filters.id) params.id = filters.id;
    if (filters.shipment) params.shipment = filters.shipment;
    if (filters.shipper_reference) params.shipper_reference = filters.shipper_reference;
    if (filters.tracking_number) params.tracking_number = filters.tracking_number;
    if (filters.receiver_name) params.receiver_name = filters.receiver_name;
    if (filters.receiver_phone_number) params.receiver_phone_number = filters.receiver_phone_number;
    if (filters.receiver_civil_id) params.receiver_civil_id = filters.receiver_civil_id;
    if (filters.destination_location_type) params.destination_location_type = filters.destination_location_type;
    if (filters.destination_location) params.destination_location = filters.destination_location;
    if (filters.payment_status) params.payment_status = filters.payment_status;
    if (filters.owner_name) params.owner_name = filters.owner_name;
    if (filters.owner_phone_number) params.owner_phone_number = filters.owner_phone_number;
    if (filters.owner_civil_id) params.owner_civil_id = filters.owner_civil_id;
    if (filterOwner?.id != null) params.owner = filterOwner.id;
    if (filters.ordering) params.ordering = filters.ordering;

    if (filters.deleted !== undefined && filters.deleted !== null) {
      params.deleted = filters.deleted === 'true' || filters.deleted === true;
    }

    if (filters.modified_at_start) {
      params.modified_at_start =
        filters.modified_at_start.length === 10
          ? `${filters.modified_at_start}T00:00:00`
          : filters.modified_at_start;
    }
    if (filters.modified_at_end) {
      params.modified_at_end =
        filters.modified_at_end.length === 10
          ? `${filters.modified_at_end}T23:59:59`
          : filters.modified_at_end;
    }

    if (Array.isArray(tagFilterNames) && tagFilterNames.length > 0) {
      const tagsQuery = await fetchTagsQueryParamFromNames(tagFilterNames);
      if (tagsQuery) params.tags = tagsQuery;
    }

    return params;
  }, [filters, filterOwner, tagFilterNames, paginationModel.page, paginationModel.pageSize]);

  const loadReceiverDetails = useCallback(async () => {
    if (!hasPermission(PERMISSIONS.RECEIVER_DETAILS_LIST)) {
      setReceiverDetails([]);
      return;
    }

    setLoading(true);
    try {
      const params = await buildListParams({ includePagination: true });
      const response = await getReceiverDetails(params);
      if (response && response.data) {
        const meta = getPaginatedMeta(response.data, paginationModel.pageSize);
        setData(meta);
        setReceiverDetails(meta.results);
      } else {
        setReceiverDetails([]);
        setData(null);
      }
    } catch (error) {
      console.error('Failed to load receiver details:', error);
      setResponseMessage(error.message || t('receiverDetailsPage.loadFailed'));
      setFailedNotification(true);
      setReceiverDetails([]);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [hasPermission, buildListParams, t]);

  useEffect(() => {
    loadReceiverDetails();
    // eslint-disable-next-line
  }, [paginationModel.page, paginationModel.pageSize]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    loadReceiverDetails();
  };

  const handlePageSizeChange = (pageSize) => {
    setPaginationModel({ page: 0, pageSize });
  };

  const handlePageChange = (page) => {
    setPaginationModel((prev) => ({ ...prev, page }));
  };

  // Create handlers
  const handleCreateClick = () => {
    setCreateForm({
      shipment: '',
      receiver_name: '',
      receiver_phone_number: '',
      receiver_civil_id: '',
      destination_location_type: '',
      destination_location: '',
    });
    setCreateFormErrors({});
    setCreateModalOpen(true);
  };

  const handleCreateSubmit = async () => {
    if (!hasPermission(PERMISSIONS.RECEIVER_DETAILS_CREATE)) return;
    // Clear previous errors
    setCreateFormErrors({});

    // Validate required fields
    if (!createForm.shipment || createForm.shipment.trim() === '') {
      setCreateFormErrors({ shipment: t('trackingEvent.errShipmentRequired') });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...createForm,
        shipment: parseInt(createForm.shipment),
      };
      if (payload.destination_location_type === '') {
        delete payload.destination_location_type;
      } else if (typeof payload.destination_location_type === 'string') {
        payload.destination_location_type = payload.destination_location_type.toLowerCase();
      }
      await createReceiverDetail(payload);
      
      setResponseMessage(t('receiverDetailsPage.createdSuccess'));
      setSuccessNotification(true);
      setCreateModalOpen(false);
      setCreateForm({
        shipment: '',
        receiver_name: '',
        receiver_phone_number: '',
        receiver_civil_id: '',
        destination_location_type: '',
        destination_location: '',
      });
      loadReceiverDetails();
    } catch (error) {
      console.error('Failed to create receiver details:', error);
      
      if (error.response && error.response.status === 400) {
        const errorData = error.response.data;
        const errors = {};
        if (errorData) {
          Object.keys(errorData).forEach(key => {
            const errorValue = errorData[key];
            const raw = Array.isArray(errorValue) ? errorValue[0] : errorValue;
            errors[key] = translateAggregateBackendMessage(String(raw), t);
          });
        }
        setCreateFormErrors(errors);
        setResponseMessage(translateApiErrorData(errorData, t) || t('receiverDetailsPage.createFailed'));
        setFailedNotification(true);
      } else {
        setResponseMessage(translateAxiosErrorMessage(error, t) || t('receiverDetailsPage.createFailed'));
        setFailedNotification(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // Edit handlers
  const handleEditClick = (receiverDetail) => {
    setEditingReceiverDetail(receiverDetail);
    setEditForm({
      shipment: (receiverDetail.shipment && receiverDetail.shipment.toString()) || '',
      receiver_name: receiverDetail.receiver_name || '',
      receiver_phone_number: receiverDetail.receiver_phone_number || '',
      receiver_civil_id: receiverDetail.receiver_civil_id || '',
      destination_location_type: receiverDetail.destination_location_type
        ? String(receiverDetail.destination_location_type).toLowerCase()
        : '',
      destination_location: receiverDetail.destination_location || '',
    });
    setEditFormErrors({});
    setEditModalOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!editingReceiverDetail) return;
    if (!hasPermission(PERMISSIONS.RECEIVER_DETAILS_UPDATE)) return;

    // Clear previous errors
    setEditFormErrors({});

    setLoading(true);
    try {
      const payload = {
        ...editForm,
        shipment: editForm.shipment ? parseInt(editForm.shipment) : editingReceiverDetail.shipment,
      };
      if (payload.destination_location_type === '') {
        delete payload.destination_location_type;
      } else if (typeof payload.destination_location_type === 'string') {
        payload.destination_location_type = payload.destination_location_type.toLowerCase();
      }

      await updateReceiverDetail(editingReceiverDetail.id, payload);
      
      setResponseMessage(t('receiverDetailsPage.updatedSuccess'));
      setSuccessNotification(true);
      setEditModalOpen(false);
      setEditingReceiverDetail(null);
      loadReceiverDetails();
    } catch (error) {
      console.error('Failed to update receiver details:', error);
      
      if (error.response && error.response.status === 400) {
        const errorData = error.response.data;
        const errors = {};
        if (errorData) {
          Object.keys(errorData).forEach(key => {
            const errorValue = errorData[key];
            const raw = Array.isArray(errorValue) ? errorValue[0] : errorValue;
            errors[key] = translateAggregateBackendMessage(String(raw), t);
          });
        }
        setEditFormErrors(errors);
        setResponseMessage(translateApiErrorData(errorData, t) || t('receiverDetailsPage.updateFailed'));
        setFailedNotification(true);
      } else {
        setResponseMessage(translateAxiosErrorMessage(error, t) || t('receiverDetailsPage.updateFailed'));
        setFailedNotification(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // Delete handlers
  const handleDeleteClick = (receiverDetail) => {
    setDeletingReceiverDetail(receiverDetail);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingReceiverDetail) return;
    if (!hasPermission(PERMISSIONS.RECEIVER_DETAILS_DELETE)) return;

    setLoading(true);
    try {
      await deleteReceiverDetail(deletingReceiverDetail.id);
      
      setResponseMessage(t('receiverDetailsPage.deletedSuccess'));
      setSuccessNotification(true);
      setDeleteModalOpen(false);
      setDeletingReceiverDetail(null);
      loadReceiverDetails();
    } catch (error) {
      console.error('Failed to delete receiver details:', error);
      setResponseMessage(translateAxiosErrorMessage(error, t) || t('receiverDetailsPage.deleteFailed'));
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  };

  // Export handler
  const handleExportToExcel = async () => {
    if (!hasPermission(PERMISSIONS.RECEIVER_DETAILS_LIST)) {
      setResponseMessage(t('receiverDetailsPage.exportNoPermission'));
      setFailedNotification(true);
      return;
    }

    const hasSelection = selectedRows.length > 0;

    setExportLoading(true);
    try {
      let response;
      if (hasSelection) {
        response = await exportReceiverDetailsToExcel({ ids: selectedRows });
      } else {
        const params = await buildListParams({ includePagination: false });
        response = await exportReceiverDetailsToExcel({ params });
      }
      
      // Create blob and download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with timestamp
      const now = new Date();
      const timestamp = now.toISOString().slice(0, 19).replace(/[:-]/g, '').replace('T', '_');
      link.download = `receiver_details_export_${timestamp}.xlsx`;
      
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      setResponseMessage(
        hasSelection
          ? t('receiverDetailsPage.exportSuccess', { count: selectedRows.length })
          : t('receiverDetailsPage.exportFilteredSuccess')
      );
      setSuccessNotification(true);
      setSelectedRows([]);
    } catch (error) {
      console.error('Failed to export receiver details:', error);
      const fallback = t('receiverDetailsPage.exportFailed');
      if (error.response) {
        const errorData = error.response.data || {};
        if (error.response.status === 404) {
          setResponseMessage(
            (errorData.error && translateAggregateBackendMessage(String(errorData.error), t)) ||
              t('receiverDetailsPage.exportNotFound')
          );
        } else {
          setResponseMessage(translateApiErrorData(errorData, t) || translateAxiosErrorMessage(error, t) || fallback);
        }
      } else {
        setResponseMessage(translateAxiosErrorMessage(error, t) || fallback);
      }
      setFailedNotification(true);
    } finally {
      setExportLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const d = new Date(dateString);
      const time = d.toLocaleTimeString('en-US', { timeZone: 'Asia/Muscat', hour12: true, hour: 'numeric', minute: 'numeric' });
      return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()} ${time}`;
    } catch (e) {
      return dateString;
    }
  };

  const paymentStatusIcon = (paymentStatus) => {
    const config = getPaymentStatusIconConfig(paymentStatus);
    return (
      <Tooltip title={config.title}>
        <MonetizationOnIcon
          color={config.color === 'success' ? 'success' : undefined}
          sx={{ color: config.color !== 'success' ? config.color : undefined }}
        />
      </Tooltip>
    );
  };

  const columns = [
    { field: 'id', headerName: t('trackingEvent.colId'), flex: 1, minWidth: 80 },
    { 
      field: 'shipment', 
      headerName: t('trackingEvent.colShipmentId'), 
      flex: 1.5, 
      minWidth: 120,
      renderCell: (params) => params.row.shipment || '-'
    },
    {
      field: 'shipper_reference',
      headerName: t('receiverDetailsPage.colShipperReference'),
      flex: 1.5,
      minWidth: 140,
      renderCell: (params) => params.row.shipper_reference || '-',
    },
    {
      field: 'tracking_number',
      headerName: t('receiverDetailsPage.colTrackingNumber'),
      flex: 1.5,
      minWidth: 140,
      renderCell: (params) => params.row.tracking_number || '-',
    },
    {
      field: 'owner_name',
      headerName: t('receiverDetailsPage.colOwnerName'),
      flex: 2,
      minWidth: 150,
      renderCell: (params) => params.row.owner_name || '-',
    },
    {
      field: 'owner_phone_number',
      headerName: t('receiverDetailsPage.colOwnerPhone'),
      flex: 1.5,
      minWidth: 130,
      renderCell: (params) => params.row.owner_phone_number || '-',
    },
    {
      field: 'items_description',
      headerName: t('receiverDetailsPage.colItemsDescription'),
      flex: 2,
      minWidth: 150,
      renderCell: (params) => params.row.items_description || '-',
    },
    {
      field: 'packages_quantity',
      headerName: t('receiverDetailsPage.colPackagesQty'),
      flex: 1,
      minWidth: 100,
      renderCell: (params) => params.row.packages_quantity ?? '-',
    },
    {
      field: 'volumetric_weight',
      headerName: t('receiverDetailsPage.colVolumetricWeight'),
      flex: 1,
      minWidth: 120,
      renderCell: (params) => params.row.volumetric_weight ?? '-',
    },
    {
      field: 'price',
      headerName: t('receiverDetailsPage.colPrice'),
      flex: 1,
      minWidth: 100,
      renderCell: (params) => params.row.price ?? '-',
    },
    {
      field: 'payment_status',
      headerName: t('receiverDetailsPage.colPaymentStatus'),
      flex: 1.5,
      minWidth: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {paymentStatusIcon(params.row.payment_status)}
          <Typography level="body-sm">{formatPaymentStatus(params.row.payment_status)}</Typography>
        </Box>
      ),
    },
    { 
      field: 'receiver_name', 
      headerName: t('customer.receiverName'), 
      flex: 2, 
      minWidth: 150,
      renderCell: (params) => params.row.receiver_name || '-'
    },
    { 
      field: 'receiver_phone_number', 
      headerName: t('customer.receiverPhone'), 
      flex: 2, 
      minWidth: 150,
      renderCell: (params) => params.row.receiver_phone_number || '-'
    },
    { 
      field: 'receiver_civil_id', 
      headerName: t('customer.receiverCivilId'), 
      flex: 2, 
      minWidth: 150,
      renderCell: (params) => params.row.receiver_civil_id || '-'
    },
    { 
      field: 'destination_location_type', 
      headerName: t('receiverDetailsPage.colLocationType'), 
      flex: 1.5, 
      minWidth: 120,
      renderCell: (params) => formatDestinationLocationTypeCell(params.row.destination_location_type)
    },
    { 
      field: 'destination_location', 
      headerName: t('receiverDetailsPage.colDestination'), 
      flex: 3, 
      minWidth: 200,
      renderCell: (params) => params.row.destination_location || '-'
    },
    { 
      field: 'created_at', 
      headerName: t('trackingEvent.colCreatedAt'), 
      flex: 2, 
      minWidth: 160,
      renderCell: (params) => formatDate(params.row.created_at)
    },
    { 
      field: 'modified_at', 
      headerName: t('trackingEvent.colModifiedAt'), 
      flex: 2, 
      minWidth: 160,
      renderCell: (params) => formatDate(params.row.modified_at)
    },
    {
      field: 'actions',
      headerName: t('trackingEvent.colActions'),
      flex: 1.5,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        const isDeleted = params.row.deleted === true || params.row.deleted === 'true';
        if (isDeleted) return null;
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {hasPermission(PERMISSIONS.RECEIVER_DETAILS_UPDATE) && (
              <Tooltip title={t('trackingEvent.tooltipEdit')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(params.row);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {hasPermission(PERMISSIONS.RECEIVER_DETAILS_DELETE) && (
              <Tooltip title={t('trackingEvent.tooltipDelete')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(params.row);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      }
    },
  ];

  return (
    <DirectionalMaterialThemeProvider>
      <CssBaseline />
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex' }} m={3}>

          <Box width={"100%"}>
            <Box sx={{ mb: 3 }}>
              <Breadcrumbs separator={<KeyboardArrowRight />} aria-label="breadcrumbs">
                <Link color="neutral" href="/shipments">{t('nav.shipments')}</Link>
                <Link color="neutral" href="/shipment/receiver-details">{t('nav.receiverDetails')}</Link>
              </Breadcrumbs>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Typography textAlign={"start"} level="h1">{t('nav.receiverDetails')}</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  {hasPermission(PERMISSIONS.RECEIVER_DETAILS_LIST) && (
                    <Button
                      startDecorator={<FileDownloadIcon />}
                      onClick={handleExportToExcel}
                      color="neutral"
                      variant="outlined"
                      loading={exportLoading}
                      disabled={exportLoading}
                    >
                      {selectedRows.length > 0 
                        ? t('receiverDetailsPage.exportSelected', { count: selectedRows.length }) 
                        : t('receiverDetailsPage.exportToExcel')}
                    </Button>
                  )}
                  {hasPermission(PERMISSIONS.RECEIVER_DETAILS_CREATE) && (
                    <Button
                      startDecorator={<PersonIcon />}
                      onClick={handleCreateClick}
                      color="primary"
                      variant="solid"
                    >
                      {t('receiverDetailsPage.addButton')}
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>

            <PermissionGate permissions={[PERMISSIONS.RECEIVER_DETAILS_LIST]}>
              {/* Filters */}
              <Grid container spacing={1} sx={{ flexGrow: 1, mb: 1 }}>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <FormControl sx={{ width: '100%' }}>
                    <FormLabel>{t('trackingEvent.searchLabel')}</FormLabel>
                    <Input
                      placeholder={t('trackingEvent.searchPlaceholder')}
                      value={filters.search}
                      onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                      startDecorator={<SearchIcon />}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <FormControl sx={{ width: '100%' }}>
                    <FormLabel>{t('trackingEvent.idLabel')}</FormLabel>
                    <Input
                      placeholder={t('trackingEvent.idPlaceholder')}
                      value={filters.id}
                      onChange={(e) => setFilters(prev => ({ ...prev, id: e.target.value }))}
                      type="number"
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <FormControl sx={{ width: '100%' }}>
                    <FormLabel>{t('trackingEvent.shipmentIdLabel')}</FormLabel>
                    <Input
                      placeholder={t('trackingEvent.shipmentIdPlaceholder')}
                      value={filters.shipment}
                      onChange={(e) => setFilters(prev => ({ ...prev, shipment: e.target.value }))}
                      type="number"
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <FormControl sx={{ width: '100%' }}>
                    <FormLabel>{t('shipments.shipperReference')}</FormLabel>
                    <Input
                      value={filters.shipper_reference}
                      onChange={(e) => setFilters(prev => ({ ...prev, shipper_reference: e.target.value }))}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <FormControl sx={{ width: '100%' }}>
                    <FormLabel>{t('shipments.trackingNumber')}</FormLabel>
                    <Input
                      value={filters.tracking_number}
                      onChange={(e) => setFilters(prev => ({ ...prev, tracking_number: e.target.value }))}
                    />
                  </FormControl>
                </Grid>
                {canListCustomers && (
                  <Grid item xs={12} sm={6} md={3} lg={2}>
                    <CustomerOptionList
                      label={t('shipments.colCustomer')}
                      defaultValue={filterOwner}
                      onChange={(event, value) => setFilterOwner(value)}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <FormControl sx={{ width: '100%' }}>
                    <FormLabel>{t('receiverDetailsPage.filterOwnerName')}</FormLabel>
                    <Input
                      value={filters.owner_name}
                      onChange={(e) => setFilters(prev => ({ ...prev, owner_name: e.target.value }))}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <FormControl sx={{ width: '100%' }}>
                    <FormLabel>{t('receiverDetailsPage.filterOwnerPhone')}</FormLabel>
                    <Input
                      value={filters.owner_phone_number}
                      onChange={(e) => setFilters(prev => ({ ...prev, owner_phone_number: e.target.value }))}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <FormControl sx={{ width: '100%' }}>
                    <FormLabel>{t('receiverDetailsPage.filterOwnerCivilId')}</FormLabel>
                    <Input
                      value={filters.owner_civil_id}
                      onChange={(e) => setFilters(prev => ({ ...prev, owner_civil_id: e.target.value }))}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <PaymentStatusOptionList
                    label={t('shipments.paymentStatus')}
                    required={false}
                    paymentStatus={filters.payment_status}
                    setPaymentStatus={(value) => setFilters(prev => ({ ...prev, payment_status: value || '' }))}
                    showAll={true}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <FormControl sx={{ width: '100%' }}>
                    <FormLabel>{t('customer.receiverName')}</FormLabel>
                    <Input
                      value={filters.receiver_name}
                      onChange={(e) => setFilters(prev => ({ ...prev, receiver_name: e.target.value }))}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <FormControl sx={{ width: '100%' }}>
                    <FormLabel>{t('customer.receiverPhone')}</FormLabel>
                    <Input
                      value={filters.receiver_phone_number}
                      onChange={(e) => setFilters(prev => ({ ...prev, receiver_phone_number: e.target.value }))}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <FormControl sx={{ width: '100%' }}>
                    <FormLabel>{t('customer.receiverCivilId')}</FormLabel>
                    <Input
                      value={filters.receiver_civil_id}
                      onChange={(e) => setFilters(prev => ({ ...prev, receiver_civil_id: e.target.value }))}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <FormControl sx={{ width: '100%' }}>
                    <FormLabel>{t('receiverDetailsPage.colLocationType')}</FormLabel>
                    <Select
                      placeholder={t('actions.all')}
                      value={filters.destination_location_type}
                      onChange={(e, value) =>
                        setFilters((prev) => ({ ...prev, destination_location_type: value || '' }))
                      }
                    >
                      <Option value="">{t('actions.all')}</Option>
                      {destinationLocationTypeOptions.map((opt) => (
                        <Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <FormControl sx={{ width: '100%' }}>
                    <FormLabel>{t('receiverDetailsPage.colDestination')}</FormLabel>
                    <Input
                      placeholder={t('receiverDetailsPage.placeholderDestinationLocation')}
                      value={filters.destination_location}
                      onChange={(e) => setFilters(prev => ({ ...prev, destination_location: e.target.value }))}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <FormControl sx={{ width: '100%' }}>
                    <FormLabel>{t('receiverDetailsPage.filterDeletedPlaceholder')}</FormLabel>
                    <Select
                      value={filters.deleted ? 'deleted' : 'not_deleted'}
                      onChange={(e, value) => {
                        setFilters(prev => ({
                          ...prev,
                          deleted: value === 'deleted',
                        }));
                      }}
                    >
                      <Option value="not_deleted">{t('receiverDetailsPage.filterNotDeleted')}</Option>
                      <Option value="deleted">{t('receiverDetailsPage.filterDeleted')}</Option>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <FormControl sx={{ width: '100%' }}>
                    <FormLabel>{t('receiverDetailsPage.modifiedFrom')}</FormLabel>
                    <Input
                      type="date"
                      value={filters.modified_at_start}
                      onChange={(e) => setFilters(prev => ({ ...prev, modified_at_start: e.target.value }))}
                      slotProps={{ input: { placeholder: t('receiverDetailsPage.modifiedFrom') } }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <FormControl sx={{ width: '100%' }}>
                    <FormLabel>{t('receiverDetailsPage.modifiedTo')}</FormLabel>
                    <Input
                      type="date"
                      value={filters.modified_at_end}
                      onChange={(e) => setFilters(prev => ({ ...prev, modified_at_end: e.target.value }))}
                      slotProps={{ input: { placeholder: t('receiverDetailsPage.modifiedTo') } }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <FormControl sx={{ width: '100%' }}>
                    <FormLabel>{t('receiverDetailsPage.orderingLabel')}</FormLabel>
                    <Select
                      value={filters.ordering}
                      onChange={(e, value) => setFilters(prev => ({ ...prev, ordering: value || '-id' }))}
                    >
                      {orderingOptions.map((opt) => (
                        <Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <ShipmentTagsOptionList
                    filterMode
                    value={tagFilterNames}
                    onChange={setTagFilterNames}
                    label={t('shipments.tagsHasAny')}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                  <Button
                    type="submit"
                    loading={loading}
                    startDecorator={<SearchIcon />}
                    sx={{ width: '100%' }}
                  >
                    {t('actions.search')}
                  </Button>
                </Grid>
              </Grid>

              {/* Table */}
              <Box>
                <DataGrid
                  slots={dataGridNativeCheckboxSlots}
                  rows={receiverDetails || []}
                  columns={columns}
                  loading={loading}
                  pagination
                  paginationModel={paginationModel}
                  paginationMode="server"
                  rowCount={data ? (data.count || receiverDetails.length) : 0}
                  onPaginationModelChange={(params) => {
                    if (params.pageSize !== paginationModel.pageSize) {
                      handlePageSizeChange(params.pageSize);
                    } else {
                      handlePageChange(params.page);
                    }
                  }}
                  pageSizeOptions={[10, 25, 50, 100]}
                  hideFooterSelectedRowCount
                  getRowHeight={() => 'auto'}
                  checkboxSelection
                  onRowSelectionModelChange={(newSelection) => {
                    setSelectedRows(newSelection);
                  }}
                  rowSelectionModel={selectedRows}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    '& .MuiDataGrid-root': {
                      border: 'none',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#f5f5f5',
                      borderBottom: '2px solid #e0e0e0',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#333',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#333',
                      },
                    },
                    '& .MuiDataGrid-cell': {
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                      lineHeight: '1.5',
                      padding: '12px 16px',
                      fontSize: '14px',
                      color: '#555',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      borderBottom: '1px solid #f0f0f0',
                    },
                    '& .MuiDataGrid-row': {
                      '&:hover': {
                        backgroundColor: '#f8f9fa',
                        cursor: 'pointer',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#e3f2fd',
                        '&:hover': {
                          backgroundColor: '#bbdefb',
                        },
                      },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: '2px solid #e0e0e0',
                      backgroundColor: '#fafafa',
                      fontSize: '14px',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    },
                    '& .MuiDataGrid-toolbarContainer': {
                      padding: '12px',
                      backgroundColor: '#fafafa',
                      borderBottom: '1px solid #e0e0e0',
                    },
                    '@media (max-width: 600px)': {
                      '& .MuiDataGrid-toolbarContainer': {
                        flexDirection: 'column',
                        gap: 1,
                      },
                      '& .MuiDataGrid-footer': {
                        flexDirection: 'column',
                        gap: 1,
                      },
                      '& .MuiDataGrid-cell': {
                        minHeight: '60px',
                        alignItems: 'center',
                        padding: '8px',
                        fontSize: '13px',
                      },
                      '& .MuiDataGrid-columnHeaders': {
                        fontSize: '13px',
                      },
                    }
                  }}
                />
              </Box>
            </PermissionGate>

            {/* Notifications */}
            <Snackbar
              autoHideDuration={3000}
              open={failedNotification}
              variant="soft"
              color="danger"
              onClose={() => setFailedNotification(false)}
            >
              {responseMessage}
            </Snackbar>
            <Snackbar
              autoHideDuration={3000}
              open={successNotification}
              variant="soft"
              color="success"
              onClose={() => setSuccessNotification(false)}
            >
              {responseMessage}
            </Snackbar>

            {/* Create Modal */}
            <Modal open={createModalOpen} onClose={() => setCreateModalOpen(false)}>
              <ModalDialog
                layout="fullscreen"
                sx={{
                  width: { xs: '100%', sm: '90%', md: '600px', lg: '700px' },
                  maxWidth: '700px',
                  maxHeight: { xs: '100vh', sm: '90vh' },
                  margin: { xs: 0, sm: 'auto' },
                  borderRadius: { xs: 0, sm: '12px' },
                  overflow: 'auto',
                }}
              >
                <ModalClose />
                <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, pb: 2 }}>
                  {t('receiverDetailsPage.modalCreateTitle')}
                </DialogTitle>
                <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
                  <Stack spacing={3} sx={{ mt: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControl error={!!createFormErrors.shipment} sx={{ width: '100%' }}>
                          <FormLabel>{t('receiverDetailsPage.shipmentIdRequired')}</FormLabel>
                          <Input
                            value={createForm.shipment}
                            onChange={(e) => {
                              setCreateForm(prev => ({ ...prev, shipment: e.target.value }));
                              if (createFormErrors.shipment) {
                                setCreateFormErrors(prev => ({ ...prev, shipment: '' }));
                              }
                            }}
                            error={!!createFormErrors.shipment}
                            type="number"
                            size="md"
                          />
                          {createFormErrors.shipment && (
                            <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                              {createFormErrors.shipment}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl error={!!createFormErrors.receiver_name} sx={{ width: '100%' }}>
                          <FormLabel>{t('customer.receiverName')}</FormLabel>
                          <Input
                            value={createForm.receiver_name}
                            onChange={(e) => {
                              setCreateForm(prev => ({ ...prev, receiver_name: e.target.value }));
                              if (createFormErrors.receiver_name) {
                                setCreateFormErrors(prev => ({ ...prev, receiver_name: '' }));
                              }
                            }}
                            error={!!createFormErrors.receiver_name}
                            size="md"
                          />
                          {createFormErrors.receiver_name && (
                            <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                              {createFormErrors.receiver_name}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl error={!!createFormErrors.receiver_phone_number} sx={{ width: '100%' }}>
                          <FormLabel>{t('customer.receiverPhone')}</FormLabel>
                          <Input
                            value={createForm.receiver_phone_number}
                            onChange={(e) => {
                              setCreateForm(prev => ({ ...prev, receiver_phone_number: e.target.value }));
                              if (createFormErrors.receiver_phone_number) {
                                setCreateFormErrors(prev => ({ ...prev, receiver_phone_number: '' }));
                              }
                            }}
                            error={!!createFormErrors.receiver_phone_number}
                            size="md"
                          />
                          {createFormErrors.receiver_phone_number && (
                            <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                              {createFormErrors.receiver_phone_number}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormLabel>{t('customer.receiverCivilId')}</FormLabel>
                          <Input
                            value={createForm.receiver_civil_id}
                            onChange={(e) => setCreateForm(prev => ({ ...prev, receiver_civil_id: e.target.value }))}
                            size="md"
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormLabel>{t('receiverDetailsPage.colLocationType')}</FormLabel>
                          <Select
                            value={createForm.destination_location_type}
                            onChange={(e, value) =>
                              setCreateForm((prev) => ({
                                ...prev,
                                destination_location_type: value || '',
                              }))
                            }
                            placeholder={t('receiverDetailsPage.selectLocationType')}
                            size="md"
                          >
                            <Option value="">{t('receiverDetailsPage.locationTypeNotSet')}</Option>
                            {destinationLocationTypeOptions.map((opt) => (
                              <Option key={opt.value} value={opt.value}>
                                {opt.label}
                              </Option>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormLabel>{t('customer.destinationField')}</FormLabel>
                          <Textarea
                            value={createForm.destination_location}
                            onChange={(e) => setCreateForm(prev => ({ ...prev, destination_location: e.target.value }))}
                            minRows={3}
                            size="md"
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Stack>
                </DialogContent>
                <DialogActions sx={{ 
                  px: { xs: 2, sm: 3 }, 
                  py: 2,
                  gap: 1,
                  flexDirection: { xs: 'column-reverse', sm: 'row' },
                  '& > button': {
                    width: { xs: '100%', sm: 'auto' },
                    minWidth: { xs: '100%', sm: '100px' }
                  }
                }}>
                  <Button 
                    variant="plain" 
                    color="neutral" 
                    onClick={() => setCreateModalOpen(false)}
                    fullWidth={{ xs: true, sm: false }}
                  >
                    {t('actions.cancel')}
                  </Button>
                  <Button 
                    onClick={handleCreateSubmit} 
                    loading={loading}
                    fullWidth={{ xs: true, sm: false }}
                  >
                    {t('trackingEvent.createButton')}
                  </Button>
                </DialogActions>
              </ModalDialog>
            </Modal>

            {/* Edit Modal */}
            <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)}>
              <ModalDialog
                layout="fullscreen"
                sx={{
                  width: { xs: '100%', sm: '90%', md: '600px', lg: '700px' },
                  maxWidth: '700px',
                  maxHeight: { xs: '100vh', sm: '90vh' },
                  margin: { xs: 0, sm: 'auto' },
                  borderRadius: { xs: 0, sm: '12px' },
                  overflow: 'auto',
                }}
              >
                <ModalClose />
                <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, pb: 2 }}>
                  {t('receiverDetailsPage.modalEditTitle')}
                </DialogTitle>
                <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
                  <Stack spacing={3} sx={{ mt: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControl error={!!editFormErrors.shipment} sx={{ width: '100%' }}>
                          <FormLabel>{t('receiverDetailsPage.shipmentIdLabelPlain')}</FormLabel>
                          <Input
                            value={editForm.shipment}
                            onChange={(e) => {
                              setEditForm(prev => ({ ...prev, shipment: e.target.value }));
                              if (editFormErrors.shipment) {
                                setEditFormErrors(prev => ({ ...prev, shipment: '' }));
                              }
                            }}
                            error={!!editFormErrors.shipment}
                            type="number"
                            size="md"
                          />
                          {editFormErrors.shipment && (
                            <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                              {editFormErrors.shipment}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl error={!!editFormErrors.receiver_name} sx={{ width: '100%' }}>
                          <FormLabel>{t('customer.receiverName')}</FormLabel>
                          <Input
                            value={editForm.receiver_name}
                            onChange={(e) => {
                              setEditForm(prev => ({ ...prev, receiver_name: e.target.value }));
                              if (editFormErrors.receiver_name) {
                                setEditFormErrors(prev => ({ ...prev, receiver_name: '' }));
                              }
                            }}
                            error={!!editFormErrors.receiver_name}
                            size="md"
                          />
                          {editFormErrors.receiver_name && (
                            <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                              {editFormErrors.receiver_name}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl error={!!editFormErrors.receiver_phone_number} sx={{ width: '100%' }}>
                          <FormLabel>{t('customer.receiverPhone')}</FormLabel>
                          <Input
                            value={editForm.receiver_phone_number}
                            onChange={(e) => {
                              setEditForm(prev => ({ ...prev, receiver_phone_number: e.target.value }));
                              if (editFormErrors.receiver_phone_number) {
                                setEditFormErrors(prev => ({ ...prev, receiver_phone_number: '' }));
                              }
                            }}
                            error={!!editFormErrors.receiver_phone_number}
                            size="md"
                          />
                          {editFormErrors.receiver_phone_number && (
                            <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                              {editFormErrors.receiver_phone_number}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormLabel>{t('customer.receiverCivilId')}</FormLabel>
                          <Input
                            value={editForm.receiver_civil_id}
                            onChange={(e) => setEditForm(prev => ({ ...prev, receiver_civil_id: e.target.value }))}
                            size="md"
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormLabel>{t('receiverDetailsPage.colLocationType')}</FormLabel>
                          <Select
                            value={editForm.destination_location_type}
                            onChange={(e, value) =>
                              setEditForm((prev) => ({
                                ...prev,
                                destination_location_type: value || '',
                              }))
                            }
                            placeholder={t('receiverDetailsPage.selectLocationType')}
                            size="md"
                          >
                            <Option value="">{t('receiverDetailsPage.locationTypeNotSet')}</Option>
                            {destinationLocationTypeOptions.map((opt) => (
                              <Option key={opt.value} value={opt.value}>
                                {opt.label}
                              </Option>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormLabel>{t('customer.destinationField')}</FormLabel>
                          <Textarea
                            value={editForm.destination_location}
                            onChange={(e) => setEditForm(prev => ({ ...prev, destination_location: e.target.value }))}
                            minRows={3}
                            size="md"
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Stack>
                </DialogContent>
                <DialogActions sx={{ 
                  px: { xs: 2, sm: 3 }, 
                  py: 2,
                  gap: 1,
                  flexDirection: { xs: 'column-reverse', sm: 'row' },
                  '& > button': {
                    width: { xs: '100%', sm: 'auto' },
                    minWidth: { xs: '100%', sm: '100px' }
                  }
                }}>
                  <Button 
                    variant="plain" 
                    color="neutral" 
                    onClick={() => setEditModalOpen(false)}
                    fullWidth={{ xs: true, sm: false }}
                  >
                    {t('actions.cancel')}
                  </Button>
                  <Button 
                    onClick={handleEditSubmit} 
                    loading={loading}
                    fullWidth={{ xs: true, sm: false }}
                  >
                    {t('actions.save')}
                  </Button>
                </DialogActions>
              </ModalDialog>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
              <ModalDialog variant="outlined" role="alertdialog">
                <DialogTitle>
                  <WarningRounded />
                  {t('trackingEvent.confirmDeleteTitle')}
                </DialogTitle>
                <Divider />
                <DialogContent>
                  {t('receiverDetailsPage.confirmDeleteBody', { shipmentId: deletingReceiverDetail && deletingReceiverDetail.shipment })}
                </DialogContent>
                <DialogActions>
                  <Button variant="plain" color="neutral" onClick={() => setDeleteModalOpen(false)}>
                    {t('actions.cancel')}
                  </Button>
                  <Button variant="solid" color="danger" onClick={handleDeleteConfirm} loading={loading}>
                    {t('actions.delete')}
                  </Button>
                </DialogActions>
              </ModalDialog>
            </Modal>
          </Box>
        </Box>
      </form>
    </DirectionalMaterialThemeProvider>
  );
}

